/* eslint-disable @typescript-eslint/member-ordering */
import foregroundEventEmitter from '../../foreground/eventEmitter';
import { getChunkScrollDepthInMobile } from '../../foreground/scrollDepth';
import { ChunkContainerElement } from '../../foreground/types/chunkedDocuments';
// eslint-disable-next-line import/no-cycle
import { findScrollTargetInChunkContainer } from '../../foreground/utils/chunkedScrolling';
import { getIsCurrentlyUsingStylus } from '../../foreground/utils/detectIfIsCurrentlyUsingStylus';
import { findAllFocusableElements } from '../../foreground/utils/findAllFocusableElements';
import {
  findCenteredElementInViewport,
} from '../../foreground/utils/findCenteredElementInViewport';
import { findChunkContainerForNode } from '../../foreground/utils/findChunkContainerForNode';
import getDeepestWith from '../../foreground/utils/getDeepestWith';
import { isNodeAnHTMLElement } from '../../foreground/utils/isNodeAnHTMLElement';
// eslint-disable-next-line import/no-cycle
import { makeWebviewLogger } from '../../foreground/utils/makeWebviewLogger';
import { isActuallyMovingWithStylus } from '../../foreground/utils/stylusEvents';
import { EpubLandmark, EpubPageListItem, LenientReadingPosition } from '../../types';
import { isHTMLElement, isTextNode } from '../../typeValidators';
import nowTimestamp from '../../utils/dates/nowTimestamp';
import { DeferredPromise } from '../../utils/DeferredPromise';
import delay from '../../utils/delay';
import exceptionHandler from '../../utils/exceptionHandler.platform';
import { handleCenteredElementDebug } from './centeredElementDebugUtils';
import { ScrollingManagerError } from './errors';
import { getScreenHeight } from './getScreenHeight';
import { animateEndOfReadingButton } from './initEndOfReading';
import { PageRect, ScrollingManager, TouchEventDisabledSource } from './ScrollingManager';
import { CLICKABLE_TAGS, PAGINATION_DOCUMENT_TOP_MARGIN } from './types';

/**
 * A ChunkRelativeBreak is a break that occurs in a chunk.
 *
 * We use this to store breaks from CSS `page-break-*` / `break-*` directives discovered in the current chunk.
 */
type ChunkRelativeBreak = {
  chunkIndex: number;
  offsetInChunk: number;
  breakType: 'before' | 'after';
};

const TWEET_CLASS_NAME = 'rw-embedded-tweet';
// Add tags here that should not have page borders cross them
const NON_SPLITTABLE_TAGS = new Set<string>(['IMG', 'VIDEO', 'FIGURE', 'TABLE', 'TR', 'TD']);
const SPLIT_BORDER_HEIGHT = 0;
const MINIMUM_PAGE_HEIGHT = 100;

type PageScrollEvent = {
  direction: 'down' | 'up' | null;
  velocity: number;
  disableAnimation?: boolean;
  specificPage?: number;
};

export class VerticalPaginatedScrollingManager extends ScrollingManager {
  currentlyScrollingBecauseOfTouch = false;
  currentChunkHeight = 0;
  currentChunkTop = 0;
  logger = makeWebviewLogger(__filename);

  selectionChangeTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  areTouchesDisabledBecauseOfSelection = false;
  previousChunkPageRects: PageRect[] = [];
  pageRects: PageRect[] = [];
  nextChunkPageRects: PageRect[] = [];
  // Chunk-relative coordinates where a hard page break must occur due
  // to CSS `page-break-*` / `break-*` directives discovered in the current chunk.
  forcedBreakCoords: ChunkRelativeBreak[] = [];

  private forcedBreaksCache: { [chunkIndex: string]: ChunkRelativeBreak[]; } = {};

  scrollStartTime = 0;
  startPageOnTouch: number | null = null;
  startTouchClientY: number | null = null;
  startScrollYOnSelect: number | null = null;
  endTouchClientY = 0;
  // Used for tracking direction changes, its throttled to help with very sensitive changed
  // ongoingTouchClientY = 0;
  touchYDirection = 0;
  startTouchX = 0;
  endTouchX = 0;
  hasTouchMoved = false;

  // Empirically determined values for determining what swipe speed warrants a page turn
  velocityThreshold = 0.4;
  verticalSwipeDistanceMinimumThreshold = 40;
  // If we swipe this far, its definitely a vertical swipe
  verticalSwipeDistanceConclusiveThreshold = 150;
  tapTimeThreshold = 200;
  horizontalSwipeDistanceThreshold = 80;

  totalVerticalMarginHeight = 0;

  scrollStepDelay = 8;

  currentlyTransitioningPages = false;
  // While we transition, we might log multiple gestures to swipe up or down
  // keep an array of all ongoing page transitions
  currentTransitionPromises: DeferredPromise<boolean>[] = [];

  currentPage = 0;
  centerElementUpdatingBlockedTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

  latestSelectionYCoord: number | null = null;
  latestSelectionAnchorRange: Range | null = null;

  initialPageOnScrollStart = 0;

  boundOnHighlightsRemoved: typeof this.onHighlightsRemoved | undefined;

  pageScrollOffset = 0;
  scrollEventsToProcess: PageScrollEvent[] = [];
  currentlyProcessingScrollEvents = false;
  deferResizeUntilAllScrollEventsAreDone = false;
  touchDirectionSwapCounter = 0;
  lastSeenRangesInTopSnapshot: Range[] = [];
  lastSeenRangesInBottomSnapshot: Range[] = [];
  _debugStartScrollToPageTime = 0;
  _debugEndScrollToPageTime = 0;
  destroy () {
    super.destroy();
    const allPageDividers = this.document.querySelectorAll<HTMLElement>('.page-divider');
    for (const divider of allPageDividers) {
      divider.classList.remove('hide-divider');
    }
    this.removeAllEventListeners();
    if (this.headerContainer) {
      this.headerContainer.style.top = '0px';
    }
    if (this.boundOnHighlightsRemoved) {
      foregroundEventEmitter.off('content-frame:highlights-removed', this.boundOnHighlightsRemoved);
    }

    this.clearForcedBreaksCache();
  }


  onContentChanged() {
    super.onContentChanged();
  }

  private clearForcedBreaksCache() {
    this.forcedBreaksCache = {};
  }

  init(): void {
    if (this.initialized) {
      return;
    }
    super.init();
    this.disableAllTouchEvents(TouchEventDisabledSource.Init);
    if (!this.documentRoot || !this.documentTextContent) {
      throw new ScrollingManagerError('No documentRoot element found');
    }
    this.setScrollingElementTop(0);
    this.addEventListener(this.document, 'touchstart', this.onTouchStart.bind(this));
    this.addEventListener(this.document, 'touchmove', this.onTouchMove.bind(this));
    this.addEventListener(this.document, 'touchend', this.onTouchEnd.bind(this));
    this.addEventListener(this.document, 'touchcancel', this.onTouchCancel.bind(this));
    this.addEventListener(this.window, 'scroll', this.onScroll.bind(this));
    this.addEventListener(this.document, 'selectionchange', this.onSelectionChange.bind(this));

    this.boundOnHighlightsRemoved = this.onHighlightsRemoved.bind(this);
    foregroundEventEmitter.on('content-frame:highlights-removed', this.boundOnHighlightsRemoved);

    this.pageHeight = getScreenHeight(this.window) - this.bottomMargin - this.topMargin;

    if (!this.documentRoot) {
      throw new ScrollingManagerError('No documentRoot element found');
    }

    this.logger.log('Init ', this.documentTextContent?.getBoundingClientRect().height);

    const bottomMargin = this.document.querySelector('.bottom-margin');
    if (!bottomMargin) {
      throw new ScrollingManagerError('No bottom margins found');
    }
    this.totalVerticalMarginHeight =
      PAGINATION_DOCUMENT_TOP_MARGIN + bottomMargin.getBoundingClientRect().height;

    requestAnimationFrame(() => {
      this.logger.log('Init complete');
      this.initialized = true;
      this.enableAllTouchEvents(TouchEventDisabledSource.Init);
      setTimeout(() => {
        this.enableScrollEvents();
      }, 200);
    });
  }

  handleChunksLoading() {
    if (this.chunksLoading > 0) {
      this.disableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    } else {
      this.enableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    }
    this.setSpinnerEnabled(this.chunksLoading > 0, false);
  }

  getScrollingElementTop() {
    return this.getScrollingElement().scrollTop - PAGINATION_DOCUMENT_TOP_MARGIN;
  }

  getScrollingElementMaxScroll() {
    const { scrollHeight } = this.getScrollingElement();
    return scrollHeight - this.totalVerticalMarginHeight;
  }

  onHighlightsRemoved() {
    this.refreshPageSnapshotsForCurrentPage();
  }

  setScrollingElementTop(newTop: number) {
    this.getScrollingElement().scrollTop = newTop + PAGINATION_DOCUMENT_TOP_MARGIN;
  }

  scrollingElementScrollTo({ top, behavior }: { top: number; behavior: 'smooth' | 'auto'; }) {
    this.getScrollingElement().scrollTo({
      top: top + PAGINATION_DOCUMENT_TOP_MARGIN,
      behavior: 'smooth',
    });
  }

  // Util function for debugging
  setLoggerShouldLog(shouldLog: boolean) {
    this.logger.shouldLog = () => shouldLog;
  }

  // util function for debugging, will allow free scrolling
  setDebugFreeScroll(enabled: boolean) {
    this.debugFreeScroll = enabled;
    if (enabled) {
      this._enableScrollingWithTouch();
    }
  }

  getScrollingElement(): HTMLElement {
    if (!this.document.scrollingElement) {
      throw new ScrollingManagerError('No documentRoot element found');
    }
    return this.document.scrollingElement as HTMLElement;
  }

  disableAllPaginationElements() {
    const allPageDividers = this.document.querySelectorAll<HTMLElement>('.page-divider');
    for (const divider of allPageDividers) {
      divider.classList.add('hide-divider');
    }
    if (!this.topPageSnapshotElement || !this.bottomPageSnapshotElement) {
      throw new ScrollingManagerError('No page content elements found');
    }
    this.topPageSnapshotElement.classList.add('hide-snapshot-content-from-selection');
    this.bottomPageSnapshotElement.classList.add('hide-snapshot-content-from-selection');
  }

  showVisiblePageDividerElements() {
    // TODO: Re-add when ready
    // if (this.window.pagination?.smoothAnimationsDisabled) {
    //   return;
    // }
    // const allDividerBorders = Array.from(this.document.querySelectorAll<HTMLElement>('.divider-border'));
    // if (!allDividerBorders) {
    //   throw new ScrollingManagerError(`No divider borders found`);
    // }
    // for (const divider of allDividerBorders) {
    //   divider.classList.add('divider-shadow');
    // }
  }

  hideVisiblePageDividerElements() {
    // TODO: Re-add when ready
    // const allDividerBorders = Array.from(this.document.querySelectorAll<HTMLElement>('.divider-border'));
    // if (!allDividerBorders) {
    //   throw new ScrollingManagerError(`No divider borders found`);
    // }
    // for (const divider of allDividerBorders) {
    //   divider.classList.remove('divider-shadow');
    // }
  }

  async queueScrollToPageEvent(event: PageScrollEvent, deferProcessing = false) {
    this.scrollEventsToProcess.push(event);
    this._debugStartScrollToPageTime = nowTimestamp();
    if (!deferProcessing) {
      return this.processAllScrollEvents();
    }
  }

  enableAllPaginationElements() {
    const allPageDividers = this.document.querySelectorAll<HTMLElement>('.page-divider');
    for (const divider of allPageDividers) {
      divider.classList.remove('hide-divider');
    }
    if (!this.topPageSnapshotElement || !this.bottomPageSnapshotElement) {
      throw new ScrollingManagerError('No page content elements found');
    }
    this.topPageSnapshotElement.classList.remove('hide-snapshot-content-from-selection');
    this.bottomPageSnapshotElement.classList.remove('hide-snapshot-content-from-selection');
    this.refreshPageSnapshotsForCurrentPage();
  }

  isRangeABelowRangeB(rangeA: Range, rangeB: Range) {
    // Hacky fix to handle iOS selection behavior:
    // After modifyOnGoingSelectionToGrabPunctuationOnIOS runs, we may have these strings:
    // rangeA: hello  (without leading quotation mark)
    // rangeB: "hello (with leading quotation mark)
    // We need to check if rangeA is below rangeB, accounting for this difference.
    if (rangeB.toString().startsWith('“')) {
      // rangeA.toString() won't have a leading “, that's why we add it here
      const textA = `“${rangeA.toString().trim()}`;
      const textB = rangeB.toString().trim();
      return textB.startsWith(textA) && textB.length > textA.length;
    }

    if (rangeA.startContainer === rangeB.startContainer && rangeA.startOffset === rangeB.startOffset) {
      // This means the end container/offset was moved
      // this means rangeB moved down from the anchor range
      return true;
    }

    return false;
  }

  async onSelectionChange() {
    if (this.currentlyTransitioningPages) {
      return;
    }
    const selection = this.document.getSelection();
    if (selection && !selection.isCollapsed) {
      // We currently are selecting
      if (this.startScrollYOnSelect === null) {
        this.startScrollYOnSelect = this.getScrollingElementTop();
      }
      this.logger.debug(
        `On selection change firing with a selection active, startScrollYOnSelect ${
          this.startScrollYOnSelect
        } getScrollTop: ${this.getScrollingElementTop()}`,
      );
      this.areTouchesDisabledBecauseOfSelection = true;
      // Get the latest Y coord of the selection
      const newRange = selection.getRangeAt(0);
      if (!this.latestSelectionAnchorRange) {
        this.latestSelectionAnchorRange = newRange.cloneRange();
      }
      const anchorRange = this.latestSelectionAnchorRange;
      const isAnchorBelowNewRange = this.isRangeABelowRangeB(anchorRange, newRange);

      if (newRange) {
        if (isAnchorBelowNewRange) {
          this.latestSelectionYCoord =
            this.getScrollingElementTop() + newRange.getBoundingClientRect().bottom;
        } else {
          this.latestSelectionYCoord =
            this.getScrollingElementTop() + newRange.getBoundingClientRect().top;
        }
      }

      this.disableAllPaginationElements();
      return;
    }
    this.logger.debug('On selection change firing with no active selection');
    if (!this.areTouchesDisabledBecauseOfSelection) {
      this.logger.debug('this selection was not related to us');
      // We never made any selection
      this.startScrollYOnSelect = null;
      this.latestSelectionAnchorRange = null;
      return;
    }
    if (this.selectionChangeTimer) {
      clearTimeout(this.selectionChangeTimer);
    }

    // At this point, the selection just ended
    if (this.startScrollYOnSelect !== null && this.latestSelectionYCoord !== null) {
      const selectionEndCoordY = this.latestSelectionYCoord;
      const newPage = this.getPageNumberFromCoordinate(selectionEndCoordY);
      // Selection was made by us
      this.startScrollYOnSelect = null;
      this.latestSelectionAnchorRange = null;
      this.latestSelectionYCoord = null;

      this.disableScrollEventsForNMilliseconds();
      // Because the page borders are not enabled yet, don't bother showing them yet
      this.enableAllPaginationElements();
      await this.queueScrollToPageEvent({ specificPage: newPage, velocity: 1, direction: null, disableAnimation: false });
    }

    this.selectionChangeTimer = setTimeout(() => {
      // Timeout so touch end / or other touch events dont register too quickly
      this.areTouchesDisabledBecauseOfSelection = false;
      this.selectionChangeTimer = undefined;
    }, 100);
  }

  // On Resize handles when the text content resizes
  // We want to take the old center element and scroll to it
  // we also need to recompute all page rect data and re-draw the pages
  async onResize(force = false) {
    if (!this.initialized) {
      return;
    }
    if (!this.documentRoot) {
      throw new ScrollingManagerError('document root not found');
    }

    const deviceWidth = this.windowWidth;
    this.leftClickAreaWidth = Math.min(deviceWidth * 0.2, 120);
    this.rightClickAreaWidth = Math.max(deviceWidth * 0.8, deviceWidth - 120);

    const newHeight = this.documentTextContent?.getBoundingClientRect().height;
    this.pageHeight = getScreenHeight(this.window) - this.bottomMargin - this.topMargin;
    if (!newHeight) {
      this.logger.log('failed getting new height for document text content');
      return;
    }
    if (Math.trunc(this.documentTextContentHeight) === Math.trunc(newHeight) && !force) {
      this.logger.log('Resize fired but we are the same height as before');
      return;
    }
    if (this.currentlyProcessingScrollEvents || this.chunksLoading) {

      /*
      If we are currently loading a chunk or processing scrolling,
      we want to make sure we don't mess those things up
      by manipulating state via the resize event;
      We must defer it until those actions are done
       */
      this.deferResizeUntilAllScrollEventsAreDone = true;
      return;
    }

    this.documentTextContentHeight = newHeight;
    this.logger.log('resize ', this.documentTextContent?.getBoundingClientRect().height);
    // Block updating the element so random scrolls won't throw us off
    const centeredElementScrollDelta = this.currentCenteredElementInfo?.scrollDelta;
    const centeredElement = this.currentCenteredElementInfo?.element;

    this.logger.debug(`OnResize: fired , ${this.getScrollingElementTop()}`);

    if (centeredElementScrollDelta === undefined || !centeredElement) {
      this.logger.debug('OnResize failed due to no center element');
      this.refreshAllPaginationElements(this.currentPage);
      return;
    }

    const currentChunkElement = this.getCurrentChunkContainer();

    this.clearForcedBreaksCache();
    this.forcedBreakCoords = this._scanForcedBreaksForAllLoadedChunks();

    const numberOfComputedPages = this.pageRects.length;
    this.pageRects = [];
    this.previousChunkPageRects = [];
    this.nextChunkPageRects = [];
    this.computePageRects(numberOfComputedPages);
    const scrollStrategy = this.currentChunkHeight === currentChunkElement.scrollHeight ? 'scrollToPage' : 'scrollToElement';
    this.currentChunkHeight = currentChunkElement.scrollHeight;
    this.currentChunkTop = this.getAbsoluteScrollTopOfRange(currentChunkElement);

    /*
      We can't easily rely on scrollDelta here to return to a good position because
      scrollDelta was relative to the previous height of the document
      We need to decide if we want to use the scrollDelta value
      if the scrollDelta value is positive, the centered element was below the page,
      most likely close to center and just scrolling to the element would be better
    */
    let elementOffset = centeredElementScrollDelta;
    if (centeredElementScrollDelta >= 0) {
      elementOffset = 0;
    }

    if (this.getScrollingElementTop() <= 0) {
      // If we are at the top, we don't need to do anything
      this.queueScrollToPageEvent({ direction: null, velocity: 0, disableAnimation: true });
      return;
    }

    if (scrollStrategy === 'scrollToElement') {
      this._scrollToRangeOrElement(centeredElement, elementOffset);
    } else {
      this.queueScrollToPageEvent({ direction: null, velocity: 0, disableAnimation: true });
    }
  }

  isTweetElement(element: Element) {
    if (element.classList.contains(TWEET_CLASS_NAME)) {
      return true;
    }

    let parentElement = element.parentElement;
    for (let i = 0; i < 7; i++) {
      if (!parentElement || parentElement === this.documentTextContent) {
        return false;
      }
      if (parentElement.classList.contains(TWEET_CLASS_NAME)) {
        return true;
      }
      parentElement = parentElement.parentElement;
    }
    return false;
  }

  // Determine if element is allowed to be split across pages
  isElementSplittable(element: Element) {
    if (element.classList.contains(TWEET_CLASS_NAME) || NON_SPLITTABLE_TAGS.has(element.nodeName)) {
      return false;
    }

    if (element.parentElement && this.isTweetElement(element.parentElement)) {
      return false;
    }

    // check if element has only one child and the child is not splittable
    if (element.childNodes.length === 1) {
      return !NON_SPLITTABLE_TAGS.has(element.childNodes[0].nodeName);
    }

    return true;
  }

  computeIntersectingRangeOffsetFromBottomOfPageRect(range: Range, pageRect: PageRect, shouldLogForDebug = false) {
    // This value offsets the bottom coordinate based on where we intersect the element
    let bottomCoordModifier = 0;

    const logger = makeWebviewLogger('computeIntersectingRangeOffsetFromBottomOfPageRect', {
      shouldLog: shouldLogForDebug,
    });
    const intersectingRect = { left: 0, right: this.windowWidth, top: pageRect.bottom, bottom: pageRect.bottom + 1 };
    logger.log('Check the intersecting rect ', intersectingRect);

    logger.log('Range to compute ', range);
    const clientRects = range.getClientRects();
    logger.log('Client rects for intersecting element ', clientRects);
    if (clientRects.length === 0) {
      // somehow we passed a range with no content
      logger.log('No client rects found ');
      return pageRect.bottom - (this.getScrollingElementTop() + range.getBoundingClientRect().top);
    }
    let rectToShow = clientRects[0];
    let clientRectIndex = 0;
      // Find which client rect no longer intersects the line
    logger.log('Loop through all rects of intersecting element until we find the intersecting line');
    while (clientRectIndex < clientRects.length) {
      const clientRect = clientRects[clientRectIndex];
      const absolutelyPositionedRect = {
        top: clientRect.top + this.getScrollingElementTop(),
        bottom: clientRect.bottom + this.getScrollingElementTop(),
        left: clientRect.left,
        right: clientRect.right,
      };
      logger.log('check text rect ', { currentRectBottom: absolutelyPositionedRect.bottom, pageRectBottom: pageRect.bottom }, {
        clientRect,
        targetRect: intersectingRect,
        pageBottom: pageRect.bottom,
      });
      if (absolutelyPositionedRect.bottom > pageRect.bottom) {
          // we now found a rect that we are no longer intersecting; or is below the target coordinate
          // take the rect we saw before it
        break;
      }
      if (clientRect.top > rectToShow.top) {
        rectToShow = clientRect;
      }
      clientRectIndex += 1;
    }

    logger.log('rect to show above border ', { rectToShow, clientRectIndex });
    if (clientRectIndex === 0) {
      // if its the first rect we matched on, just cover up the entire element or range
      if (isHTMLElement(range.startContainer)) {
        bottomCoordModifier = pageRect.bottom - this.getAbsoluteScrollTopOfRange(range.startContainer);
      } else {
        // fallback case which should probably never happen
        bottomCoordModifier = pageRect.bottom - (this.getScrollingElementTop() + range.getBoundingClientRect().top);
      }
    } else if (clientRectIndex >= clientRects.length && rectToShow !== clientRects[clientRects.length - 1]) {
      // We went through all the rects but found no valid intersection; chances are we are not intersecting anything
      bottomCoordModifier = 0;
    } else {
      bottomCoordModifier = pageRect.bottom - (this.getScrollingElementTop() + rectToShow.bottom);
      // TODO: AT some point we want to address the large space below each page, all of this code should change
      if (bottomCoordModifier < -50) {
        bottomCoordModifier = pageRect.bottom - (this.getScrollingElementTop() + rectToShow.top);
      }
    }
    logger.log('bottom coord modifier ', bottomCoordModifier);
    return bottomCoordModifier;
  }

  findElementThatIntersectsBottomOfPageRect(
    elementAtBottomParam: Element,
    pageRect: PageRect,
    shouldLogForDebug = false,
  ): [Element, number] {
    const documentRoot = this.documentRoot;
    if (!documentRoot) {
      throw new ScrollingManagerError('Document Text Container not found');
    }
    // Determine where we intersect
    // if we are looking at a bottom element and it intersects, we determine line height
    // and figure out how many lines of text are visible, and move the new bottom divider to show the right amount of lines

    const logger = makeWebviewLogger('findElementThatIntersectsBottomOfPageRect', {
      shouldLog: shouldLogForDebug,
    });

    // This value offsets the bottom coordinate based on where we intersect the element
    let bottomCoordModifier = 0;

    let elementThatIntersects = elementAtBottomParam;
    const bannedElements = new Set(['TD']);
    const matcherFunc = (element: Node): element is HTMLElement => {

      return (
        !isTextNode(element) &&
        isHTMLElement(element) &&
        !bannedElements.has(element.nodeName) &&
          // ignore rw highlight elements
        !element.classList.contains('rw-highlight-icon-wrapper') &&
        !element.classList.contains('rw-highlight-resize-handle') &&
        element.getBoundingClientRect().height > 0 &&
        element.getBoundingClientRect().width > 0 &&
        this.isElementIntersectingAtYCoord(element, pageRect.bottom)
      );
    };


    // If the element has children, we need to find the deepest child that intersects the bottom of the page
    // unless the current element contains direct text nodes
    if (elementThatIntersects.children.length && !this.doesElementContainDirectTextNodes(elementThatIntersects)) {
      const deepestChildThatIntersects = getDeepestWith<HTMLElement>(elementThatIntersects, matcherFunc);
      if (deepestChildThatIntersects && !isTextNode(deepestChildThatIntersects)) {
        logger.log('We found the deepest child node!');
        logger.log(deepestChildThatIntersects);
        elementThatIntersects = deepestChildThatIntersects;
      }
    }

    // if the element has text nodes, we possibly intersect a text node, we should get the line height and move the page boundary to not cut text
    // If the element does not have direct text node children, we probably did not find the deepest element intersecting the page boundary, no need to move the page
    if (this.doesElementContainDirectTextNodes(elementThatIntersects)) {
      logger.log('------ element that intersects has a text node -------------');
      logger.log(elementThatIntersects);

      const range = new Range();
      range.selectNodeContents(elementThatIntersects);

      const intersectingRect = { left: 0, right: this.windowWidth, top: pageRect.bottom, bottom: pageRect.bottom + 1 };
      logger.log('Check the intersecting rect ', intersectingRect);
      bottomCoordModifier = this.computeIntersectingRangeOffsetFromBottomOfPageRect(range, pageRect, shouldLogForDebug);
    }
    logger.log(`final bottom margin modifier ${bottomCoordModifier}`);
    logger.log('-----------------');

    return [elementThatIntersects, bottomCoordModifier];
  }

  /**
   * @param currentPageNum the current page number to compute
   * @param currentPageRect the info of the page right above the pageNUm
   * @return new page rect for pageNum
   */
  computePageRectsForNextPage(
    currentPageNum: number,
    currentPageRect: PageRect,
    shouldLogForDebug = false,
    forcedBreaks: ChunkRelativeBreak[] = [],
  ): PageRect {
    const logger = makeWebviewLogger('computePageRectsForNextPage', { shouldLog: shouldLogForDebug });
    logger.log(`Compute page rect for page ${currentPageNum + 1}, currentPageRect: `, { ...currentPageRect });
    const documentTextContent = this.documentTextContent;
    if (!documentTextContent) {
      throw new ScrollingManagerError('Document Text Container not found');
    }
    const relativeTopOfDocumentTextContent = this.getAbsoluteScrollTopOfRange(documentTextContent);
    logger.log(`finding element at the bottom of page ${currentPageNum}, currentPageRectBottom: `, currentPageRect.bottom);
    const intersectingRange = this.getRangeAtY(currentPageRect.bottom);
    // IF element At top or bottom is null, that's great, the border height is already determined

    // This value offsets the bottom coordinate based on where we intersect the element
    let bottomCoordModifier = 0;
    let elementAtBottom = null;
    if (intersectingRange) {
      elementAtBottom = this.getFirstNodeInRange(intersectingRange);
    }

    logger.log(`Starting with element at bottom of page ${currentPageNum}`, intersectingRange);

    if (elementAtBottom && isHTMLElement(elementAtBottom) && elementAtBottom.getBoundingClientRect().height > 0) {
      [elementAtBottom, bottomCoordModifier] = this.findElementThatIntersectsBottomOfPageRect(
          elementAtBottom,
          currentPageRect,
          shouldLogForDebug,
        );
      logger.log(
          'New element at bottom that intersects the bottom of the page and new page bottom',
        {
          elementAtBottom,
          bottomCoordModifier,
          currentPageBottom: currentPageRect.bottom,
        },
        );
    } else if (intersectingRange && isTextNode(elementAtBottom) && intersectingRange?.getClientRects().length > 0) {
      // we are intersecting a direct text node, rare but happens
      bottomCoordModifier = this.computeIntersectingRangeOffsetFromBottomOfPageRect(intersectingRange, currentPageRect, shouldLogForDebug);
    }

    currentPageRect.bottom -= bottomCoordModifier;
    logger.log('New page bottom ', { currentPageBottom: currentPageRect.bottom });
    if (elementAtBottom && isHTMLElement(elementAtBottom) && !this.isElementSplittable(elementAtBottom)) {
      logger.log(`element at bottom is not splittable`);
      const visibleHeightOfElementAtBottomOnThisPage =
        currentPageRect.bottom - this.getAbsoluteScrollTopOfRange(elementAtBottom);
      const newPossibleBottom = currentPageRect.bottom - visibleHeightOfElementAtBottomOnThisPage;
      const shouldPushElementToNextPage =
        !this.isTweetElement(elementAtBottom) &&
        newPossibleBottom - currentPageRect.top > MINIMUM_PAGE_HEIGHT;

      logger.log(`should push element to next page? ${shouldPushElementToNextPage}`);
      if (shouldPushElementToNextPage) {
        currentPageRect.bottom = newPossibleBottom;
        this._calculateAndApplyDividerHeight(currentPageRect, currentPageNum, relativeTopOfDocumentTextContent, visibleHeightOfElementAtBottomOnThisPage);
      } else {
        currentPageRect.bottomPageDividerHeight = SPLIT_BORDER_HEIGHT;
      }
    } else {
      this._calculateAndApplyDividerHeight(currentPageRect, currentPageNum, relativeTopOfDocumentTextContent);
    }

    if (forcedBreaks.length) {
      // To not have to search through all the breaks, we can limit the search to the breaks that are within the current page.
      // To do this, we need to find the first break that is greater than the current page top and less than the current page bottom.
      const minBottom = currentPageRect.top + MINIMUM_PAGE_HEIGHT;
      const maxBottom = currentPageRect.bottom;

      const filteredBreaks = forcedBreaks.filter((breakCoord) =>
        !this._isBreakRedundant(breakCoord, currentPageRect));

      // We now convert the breaks to absolute coordinates instead of the chunk relative coordinates.
      // This is because we need to compare them to the current page rect, which is in absolute coordinates.
      const currentChunkBreaks = filteredBreaks
        .map((breakCoord) => this._getAbsoluteCoordFromChunkRelativeBreak(breakCoord))
        .sort((a, b) => a - b);

      // We now find the first break that is greater than the current page top and less than the current page bottom.
      // If it is greater than the minimum page height, we use it as the new page bottom.
      // Otherwise, we use the current page bottom.
      // This is basically the core of the logic: do we have a CSS break that is within the current page?
      // If so, we use it as the new page bottom.
      // Otherwise, we use the current page bottom.
      let breakIndex = -1;
      for (let i = 0; i < currentChunkBreaks.length; i++) {
        const breakCoord = currentChunkBreaks[i];
        if (breakCoord > currentPageRect.top && breakCoord <= maxBottom) {
          if (breakCoord >= minBottom) {
            breakIndex = i;
            break;
          }
        }
      }

      // If we found a break, we use it as the new page bottom.
      if (breakIndex >= 0) {
        currentPageRect.bottom = currentChunkBreaks[breakIndex];
        this._calculateAndApplyDividerHeight(currentPageRect, currentPageNum, relativeTopOfDocumentTextContent);
      } else {
        logger.log(`ComputePage: No suitable CSS break found for page ${currentPageNum}`);
      }
    } else {
      logger.log(`ComputePage: No forced breaks available for page ${currentPageNum}`);
    }

    // add new border for this page
    return {
      top: currentPageRect.bottom,
      bottom: currentPageRect.bottom + this.pageHeight,
      bottomPageDividerHeight: 0,
    };
  }

  buildFirstPageRect(containerElement: ChunkContainerElement): PageRect {
    const absoluteTopOfElement = this.getAbsoluteScrollTopOfRange(containerElement);
    const pageTop = Math.trunc(absoluteTopOfElement);
    let bottom = Math.trunc(pageTop + this.pageHeight);
    if (containerElement.dataset.chunkIndex === '0') {
      const firstPageBottom = Math.max(Math.trunc(this.pageHeight + this.topMargin));
      if (firstPageBottom >= pageTop) {
        bottom = firstPageBottom;
      }
    }
    return {
      top: pageTop,
      bottom,
      bottomPageDividerHeight: 0,
    };
  }

  modifyInPlaceFinalPageRectToReachBottomOfChunk(finalPageRect: PageRect, bottomOfChunk: number) {
    finalPageRect.bottom = bottomOfChunk;
    const valueToReach = this.pageHeight + finalPageRect.top + this.bottomMargin;
    finalPageRect.bottomPageDividerHeight = valueToReach - finalPageRect.bottom + 100;
  }

  computePageRectsForChunk(
    chunkIndex: number,
    rectIndexToReach: number,
    pageRects: PageRect[],
    shouldLogForDebug = false,
) {
    const start = nowTimestamp();
    const logger = makeWebviewLogger('computePageRectsForChunk', { shouldLog: shouldLogForDebug });
    const chunkElement = this.getChunkContainerByIndex(chunkIndex);
    let bottomOfChunk = this.getAbsoluteClientRectOfElement(chunkElement).bottom;

    const chunkBreaks = this.forcedBreakCoords.filter(
      (breakCoord) => breakCoord.chunkIndex === chunkIndex,
    );


    if (rectIndexToReach < pageRects.length) {
      logger.log(`No need to compute for chunk: ${chunkIndex}, we have page margin data up to page ${pageRects.length}`);
      return;
    }
    if (pageRects.length > 0 && pageRects[pageRects.length - 1].bottom >= bottomOfChunk) {
      logger.log(`No need to compute for chunk: ${chunkIndex}, we reached the bottom of the chunk`);
      this.modifyInPlaceFinalPageRectToReachBottomOfChunk(pageRects[pageRects.length - 1], bottomOfChunk);
      return;
    }

    if (chunkIndex + 1 < this.numberOfChunks) {
      const nextChunk = this.getChunkContainerByIndex(chunkIndex + 1);
      bottomOfChunk = this.getAbsoluteScrollTopOfRange(nextChunk);
    }

    const rectIndexToStartAt = pageRects.length - 1;

    if (pageRects[rectIndexToStartAt].bottom >= bottomOfChunk) {
      logger.log(`No need to compute page rects for chunk, we are at bottom `, { pageRect: { ...pageRects[rectIndexToStartAt], rectIndexToStartAt, bottomOfChunk } });
      this.modifyInPlaceFinalPageRectToReachBottomOfChunk(pageRects[rectIndexToStartAt], bottomOfChunk);
      return;
    }

    for (let i = rectIndexToStartAt; i < rectIndexToReach; i++) {
      const currentPageRect = pageRects[i];
      logger.log(`current page to compute ${i} currentPageRectBottom ${currentPageRect.bottom}, bottomOfChunk ${bottomOfChunk}`, { pageRect: { ...currentPageRect } });
      if (currentPageRect.bottom >= bottomOfChunk - 1) {
        logger.log(`current page rect bottom ${currentPageRect.bottom} is past the bottom of the chunk: ${bottomOfChunk}, we are done`);
        this.modifyInPlaceFinalPageRectToReachBottomOfChunk(pageRects[i], bottomOfChunk);
        break;
      }
      // start from previous page margin, compute the start of that page
      // then go down to the bottom of the page and figure out where that start
      const newPageRect = this.computePageRectsForNextPage(i, currentPageRect, shouldLogForDebug, chunkBreaks);
      if (currentPageRect.bottom <= currentPageRect.top) {
        logger.warn(`Empty page! page${i}, chunkIndex: ${chunkIndex}`);
        break;
      }
      if (newPageRect.bottom <= currentPageRect.bottom) {
        exceptionHandler.captureException(new Error(`We somehow got a smaller coord for next page page${i}, chunkIndex: ${chunkIndex}`));
        // throw new ScrollingManagerError(`We somehow got a smaller coord for next page page${i}, chunkIndex: ${chunkIndex}`);
        break;
      }
      pageRects.push(newPageRect);
    }
    logger.log(`Done computing page rects for chunk: ${chunkIndex} time: `, nowTimestamp() - start, { numberOfPagesComputed: rectIndexToReach - rectIndexToStartAt });
  }

  /*
    This is the meat of the paginated manager
    We generate pages up to pageNumToComputeTo,
    if we already have some pages to generate, we reuse their values (memoization essentially)
    otherwise, go through each page, looking at elements intersecting the bottom of the page
    if there are elements, do some math to determine where the page border will be drawn
   */
  computePageRects(pageNum = 0, shouldLogForDebug = false) {
    const logger = makeWebviewLogger('computePageRects', { shouldLog: shouldLogForDebug });

    const pageNumToReach = pageNum + 3;

    if (shouldLogForDebug) {
      logger.log(`Compute up to page ${pageNumToReach}`);
    }

    const chunkElement = this.getCurrentChunkContainer();
    if (this.pageRects.length === 0) {
      this.pageRects = [this.buildFirstPageRect(chunkElement)];
    }

    logger.log(
      `going from ${this.pageRects.length} pages to ${pageNumToReach} pages `,
    );
    this.computePageRectsForChunk(
      this.currentChunkIndex,
      pageNumToReach,
      this.pageRects,
      shouldLogForDebug,
    );
  }

  showDebugStyles(enabled: boolean) {
    if (enabled) {
      this.document.body.classList.add('debug-styles');
    } else {
      this.document.body.classList.remove('debug-styles');
    }
  }

  updateCurrentCenteredElement() {
    this.logger.debug('UpdateCurrentCenteredElement fired');
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('UpdateCurrentCenteredElement Document Text Container not found');
    }

    const currentChunkContainer = this.getCurrentChunkContainer();

    if (!this.focusableElements.length) {
      this.focusableElements = findAllFocusableElements(currentChunkContainer);
    }
    let centeredElement = findCenteredElementInViewport(this.focusableElements, this.window);

    if (!centeredElement) {
      centeredElement = findScrollTargetInChunkContainer(currentChunkContainer);
    }

    this.logger.debug('updateCurrentCenteredElement ', {
      centeredElement,
      top: centeredElement?.getBoundingClientRect().top,
    });

    handleCenteredElementDebug(centeredElement, this.window, this.document);

    // Uncomment for debug purposes, this will color the center element
    // const prevElementDebug = this.document.querySelector('.centeredElementDebug');
    // prevElementDebug?.classList.remove('centeredElementDebug');
    // centeredElement?.classList.add('centeredElementDebug');
    // // // this.logger.log(centeredElement);
    // // // this.logger.log('remember to comment me back out');

    if (!centeredElement) {
      throw new ScrollingManagerError('UpdateCurrentCenteredElement no centered element found');
    }

    // Scroll delta relative to the current page we are on, -1 because otherwise the offset is the page above
    const elementOffsetFromTopOfPage =
      this.getAbsoluteScrollTopOfRange(centeredElement) -
      this.getBoundedPageRect(this.currentPage).top -
      1;

    this.currentCenteredElementInfo = {
      element: centeredElement,
      scrollDelta: elementOffsetFromTopOfPage,
    };
  }

  exponentialDecayArray(
    initialVelocity: number,
    startDistance: number,
    targetDistance: number,
    totalTime: number,
    steps: number,
  ) {
    // ChatGPT helped me here, generate an array of values that simulate an exponential decay function
    const decayConstant = -Math.log(initialVelocity / (startDistance - targetDistance)) / totalTime;
    const values = [];
    const stepTime = totalTime / steps;

    for (let i = 0; i <= steps; i++) {
      const t = i * stepTime;
      const value = targetDistance + (startDistance - targetDistance) * Math.exp(-decayConstant * t);
      values.push(value);
    }

    return values;
  }

  async fakeSmoothScrollToCoord(targetScrollTop: number, velocity = 1) {
    const currentTransitionPromise = new DeferredPromise<boolean>();
    this.currentTransitionPromises.push(currentTransitionPromise);

    const currentScrollTop = this.getScrollingElementTop();
    const delta = currentScrollTop - targetScrollTop;
    // if delta is positive, we are scrolling up, if delta is negative we are scrolling down
    if (delta === 0) {
      return;
    }
    const steps = Math.ceil(this.scrollTimeForPage / this.scrollStepDelay);
    let expDecayPoints: number[] = [];
    if (currentScrollTop < targetScrollTop) {
      const generatedExpPoints = this.exponentialDecayArray(
        Math.abs(velocity),
        targetScrollTop,
        currentScrollTop,
        this.scrollTimeForPage,
        steps,
      );
      const deltaPoints = generatedExpPoints.map((p) => targetScrollTop - p);
      expDecayPoints = deltaPoints.map((dp) => currentScrollTop + dp);
    } else {
      expDecayPoints = this.exponentialDecayArray(
        Math.abs(velocity),
        currentScrollTop,
        targetScrollTop,
        this.scrollTimeForPage,
        steps,
      );
    }

    let i = 0;
    const move = (d: number, num: number, targetScrollTop: number) => {
      if (currentTransitionPromise.status === 'resolved') {
        return;
      }
      if (d < 0) {
        this.setScrollingElementTop(Math.min(num, targetScrollTop));
      } else {
        this.setScrollingElementTop(Math.max(num, targetScrollTop));
      }
    };
    for (const num of expDecayPoints) {
      requestAnimationFrame(() => {
        move(delta, num, targetScrollTop);
      });
      i += 1;
      if (i > expDecayPoints.length * 0.9) {
        break;
      }
      await delay(this.scrollStepDelay);
    }
    if (this.window.osType === 'ios') {
      this.scrollingElementScrollTo({ top: targetScrollTop, behavior: 'smooth' });
    } else {
      this.setScrollingElementTop(targetScrollTop);
    }
    currentTransitionPromise.resolve(true);
    if (this.currentTransitionPromises.length > 20) {
      this.currentTransitionPromises.slice(this.currentTransitionPromises.length - 2);
    }
  }

  // Take a node and a page number, and fill that node with cloned elements from the page
  populateNodeWithElementsFromPage(contentChildNode: Node, allVisibleRanges: Range[], pageNum: number, shouldLogForDebug = false) {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('PopulateNodeWithElementsFromPage: no document text content found');
    }
    const logger = makeWebviewLogger('populateNodeWithElementsFromPage', { shouldLog: shouldLogForDebug });
    logger.log(`pageRect for page ${pageNum}`, this.getBoundedPageRect(pageNum));
    logger.log(`getting all visible ranges`, allVisibleRanges);

    if (allVisibleRanges.length === 0) {
      return allVisibleRanges;
    }
    let currentChunkContainer = findChunkContainerForNode(
      this.getFirstNodeInRange(allVisibleRanges[0]),
      this.documentTextContent,
    );
    const createChunkBorderDiv = () => {
      const div = this.document.createElement('div');
      div.classList.add('chunk-boundary-border');
      return div;
    };

    for (const range of allVisibleRanges) {
      const elementInRange = this.getFirstNodeInRange(range);
      logger.log(`found element in range `, elementInRange);
      if (!elementInRange) {
        continue;
      }
      const newChunkContainer = findChunkContainerForNode(
        elementInRange,
        this.documentTextContent,
      );
      if (newChunkContainer?.dataset.chunkIndex !== currentChunkContainer?.dataset.chunkIndex) {
        logger.log(`new chunk is not the same as old chunk `, { newChunkContainer, currentChunkContainer, elementInRange });
        if (contentChildNode.lastChild && isNodeAnHTMLElement(contentChildNode.lastChild)) {
          contentChildNode.lastChild.style.marginBottom = '0px';
        }
        contentChildNode.appendChild(createChunkBorderDiv());
        currentChunkContainer = newChunkContainer;
      }
      if (isNodeAnHTMLElement(elementInRange)) {
        const clonedNode = elementInRange.cloneNode(true) as HTMLElement;
        const rwHighlights = clonedNode.querySelectorAll('rw-highlight') ?? [];
        for (const highlight of rwHighlights) {
          highlight.setAttribute('data-highlight-id', `none`);
        }
        contentChildNode.appendChild(clonedNode);
      } else if (isTextNode(elementInRange)) {
        const textContent = elementInRange.textContent;
        if (!textContent) {
          continue;
        }
        const textNode = document.createTextNode(textContent);
        contentChildNode.appendChild(textNode);
      }
    }
    return allVisibleRanges;
  }

  areTwoRangeArraysEqual(rangeArrayA: Range[], rangeArrayB: Range[]) {
    if (rangeArrayA.length !== rangeArrayB.length) {
      return false;
    }
    for (let i = 0; i < rangeArrayA.length; i++) {
      const rangeA = rangeArrayA[i];
      const rangeB = rangeArrayB[i];
      if (
        rangeA.startContainer !== rangeB.startContainer ||
        rangeA.endContainer !== rangeB.endContainer ||
        rangeA.commonAncestorContainer !== rangeB.commonAncestorContainer ||
        rangeA.startOffset !== rangeB.startOffset ||
        rangeA.endOffset !== rangeB.endOffset
      ) {
        return false;
      }
    }
    return true;
  }

  // This function takes a page number, and re-creates that page dynamically in either the top or bottom snapshot container
  matchPageSnapshotToPage(element: 'top' | 'bottom', pageNum: number, offset = 0, force = false, shouldLogForDebug = false) {
    const logger = makeWebviewLogger('matchPageSnapshotToPage', { shouldLog: shouldLogForDebug });
    logger.log(`Matching ${element} to page ${pageNum}`);
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('No document text content found');
    }
    const pageSnapshotElement =
      element === 'top' ? this.topPageSnapshotElement : this.bottomPageSnapshotElement;

    if (!pageSnapshotElement) {
      throw new ScrollingManagerError('No page snapshot elements found');
    }
    // Clear out any old logic that hides this snapshot container
    pageSnapshotElement.classList.remove('hide-snapshot-content');
    pageSnapshotElement.style.height = '0px';

    const allVisibleRanges = this.getAllRangesInRect(this.getBoundedPageRect(pageNum), this.pageHeight);
    let allRangesInSnapshot = allVisibleRanges;
    const previousRanges = element === 'top' ? this.lastSeenRangesInTopSnapshot : this.lastSeenRangesInBottomSnapshot;
    if (!this.areTwoRangeArraysEqual(previousRanges, allRangesInSnapshot) || force) {
      // Delete old child
      let childClassList = '';
      if (pageSnapshotElement.children[0]) {
        childClassList = pageSnapshotElement.children[0].classList.toString();
        pageSnapshotElement.removeChild(pageSnapshotElement.children[0]);
      }

      // Create the inner child again
      const contentChild = this.document.createElement('div');
      contentChild.className = childClassList;
      pageSnapshotElement.appendChild(contentChild);
      allRangesInSnapshot = this.populateNodeWithElementsFromPage(contentChild, allVisibleRanges, pageNum, shouldLogForDebug);
    }
    if (element === 'top') {
      this.lastSeenRangesInTopSnapshot = allRangesInSnapshot;
    } else {
      this.lastSeenRangesInBottomSnapshot = allRangesInSnapshot;
    }
    const contentChild = pageSnapshotElement.childNodes[0] as HTMLElement;
    contentChild.style.top = '0px';


    // If the border above this page is non-existent, we don't need this snapshot
    if (element === 'bottom' && this.getBoundedPageRect(pageNum - 1).bottomPageDividerHeight === SPLIT_BORDER_HEIGHT) {
      pageSnapshotElement.classList.add('hide-snapshot-content');
      return;
    }
    // If the border below this page is non-existent, we don't need this snapshot
    if (
      element === 'top' &&
      this.getBoundedPageRect(pageNum).bottomPageDividerHeight ===
        SPLIT_BORDER_HEIGHT
    ) {
      pageSnapshotElement.classList.add('hide-snapshot-content');
      return;
    }

    const topOfContent = this.getAbsoluteScrollTopOfRange(this.documentTextContent);
    logger.log(`top of content ${topOfContent}`);
    // get a position relative to the text content
    const tempTopPagePos = Math.max(0, this.getBoundedPageRect(pageNum).top - topOfContent);
    logger.log(`temp top page Pos ${tempTopPagePos}`);

    pageSnapshotElement.setAttribute('current-page', `${pageNum}`);

    // first, make the snapshot the same height as the page
    // then make the snapshot stretch to either above border or below border
    // Take this example
    /*
      --------- Border (Not existent) ------------
          ------- Top Snapshot ------
          -------- Border -------------
         --------current page -------

         The top snapshot needs to be extra high to cover the content that the border otherwise would have covered
         the same idea applies to the bottom snapshot
     */
    // ONE EXCEPTION IS IF THE TOP SNAPSHOT IS FOR PAGE ZERO

    pageSnapshotElement.style.height = `${
      this.getBoundedPageRect(pageNum).bottom - this.getBoundedPageRect(pageNum).top + 1
    }px`;
    let topPageHeightModifier = 0;
    if (element === 'top') {
      // If the border above the fold is zero, that means the snapshot won't reach the top of the screen
      // and a bit of true content will peek. Double the height of the snapshot to hide this
      // unlike for the "bottom" element the top is a bit tricky
      // we will need to move the snapshot element up by the same amount we stretched it
      // this all happens lower down in this file
      const borderAboveThisPageHeight = this.getBoundedPageRect(pageNum - 1).bottomPageDividerHeight;
      if (borderAboveThisPageHeight === 0) {
        topPageHeightModifier = this.pageHeight;
      }
      pageSnapshotElement.style.height = `${
        this.getBoundedPageRect(pageNum).bottom - this.getBoundedPageRect(pageNum).top + topPageHeightModifier + 1
      }px`;
    } else if (element === 'bottom') {
      const borderBelowThisPageHeight = this.getBoundedPageRect(pageNum).bottomPageDividerHeight;
      // If the border below the fold is zero, that means the snapshot wont reach the bottom of the screen
      // and a bit of true content will peek. Double the height of the snapshot to hide this
      const modifier = borderBelowThisPageHeight === 0 ? 2 : 1;
      pageSnapshotElement.style.height = `${
        (this.getBoundedPageRect(pageNum).bottom - this.getBoundedPageRect(pageNum).top) * modifier + 1
      }px`;
    }
    // move the snapshot to the start of the page we are simulating
    // Here is where we move the top snapshot element up by however much we increased its height by
    pageSnapshotElement.style.top = `${tempTopPagePos + offset - topPageHeightModifier}px`;


    let childOffset = 0;
    // Now that we created all the children, we actually need to move the content inside the snapshot by the offset provided
    if (allRangesInSnapshot.length > 0) {
      // const firstVisibleElementRect = this.getEfficientClientRectFromRange(allRangesInSnapshot[0]);
      let firstVisibleElementRect = this.getEfficientClientRectFromRange(allRangesInSnapshot[0]);
      // get the first truly visible range
      // the reason we need to do this is that sometimes invisible elements might render incorrectly
      // in the root document vs the snapshot, and create incorrect boundary calculations
      // almost always though this loop will terminate after the first tick
      for (const range of allRangesInSnapshot) {
        const rangeRect = this.getEfficientClientRectFromRange(range);
        if (rangeRect.height > 0) {
          firstVisibleElementRect = rangeRect;
          logger.log(
            `First visible element range in real document`,
            this.getFirstNodeInRange(range),
            range,
            firstVisibleElementRect.top,
          );
          break;
        }
      }


      let firstVisibleChildNodeInSnapshot = contentChild.childNodes[0];
      for (const child of contentChild.childNodes) {
        if (isHTMLElement(child) && child.classList.contains('chunk-boundary-border')) {
          continue;
        }
        const childRange: Range = new Range();
        childRange.selectNode(child);
        const rangeRect = this.getEfficientClientRectFromRange(childRange);
        if (rangeRect.height > 0) {
          firstVisibleChildNodeInSnapshot = child;
          break;
        }
      }
      const visibleSnapshotChildRange: Range = new Range();
      visibleSnapshotChildRange.selectNode(firstVisibleChildNodeInSnapshot);
      const firstVisibleSnapshotChildRect = this.getEfficientClientRectFromRange(visibleSnapshotChildRange);
      logger.log(
        `First visible element and range in snapshot`,
        firstVisibleChildNodeInSnapshot,
        visibleSnapshotChildRange,
        firstVisibleSnapshotChildRect.top,
      );

      childOffset =
        firstVisibleElementRect.top -
        firstVisibleSnapshotChildRect.top;
      logger.log(`initial child offset comp ${childOffset}`);

      const pageBelow = Math.min(this.pageRects.length, pageNum + 1);
      logger.log(`The page below is ${pageBelow}`, this.pageRects[pageBelow]);
      const topElementOffset = this.getBoundedPageRect(pageNum).bottomPageDividerHeight;
      const bottomElementOffset = this.getBoundedPageRect(pageNum - 1).bottomPageDividerHeight;

      if (element === 'top') {
        // If we are simulating the top element, we want to move the content UP by the height of the border of the page below
        childOffset -= topElementOffset;
        // if the border is empty, no need for this, slightly redundant code, technically we shouldnt reach here
        if (topElementOffset === 0) {
          pageSnapshotElement.classList.add('hide-snapshot-content');
        }
        logger.log(`Top Element offset ${topElementOffset}`);
      } else {
        // If we are simulating the bottom element, we want to move the content down by the height of the border of the page above
        childOffset += bottomElementOffset;
        // if the border is empty, no need for this, slightly redundant code, technically we shouldnt reach here
        if (bottomElementOffset === 0) {
          pageSnapshotElement.classList.add('hide-snapshot-content');
        }
        logger.log(`bottom Element offset ${bottomElementOffset}`);
      }
    }
    contentChild.style.top = `${childOffset}px`;

    return tempTopPagePos + offset;
  }

  refreshPageSnapshotsForPage(newPage: number, force = false) {
    // re-simulate top and bottom content snapshots
    // Move the top content snapshot above this current page and offset it by the border size at the top
    this.matchPageSnapshotToPage(
        'top',
        newPage - 1,
        -this.getBoundedPageRect(newPage - 1).bottomPageDividerHeight,
        force,
      );
    // Move the bottom content snapshot below this current page and offset it by the border size at the bottom of this page
    this.matchPageSnapshotToPage(
        'bottom',
        newPage + 1,
        this.getBoundedPageRect(newPage).bottomPageDividerHeight,
        force,
      );

    if (!this.bottomPageSnapshotElement || !this.topPageSnapshotElement) {
      throw new ScrollingManagerError(
        '[RefreshPageSnapshotsForPage] No page snapshot elements found',
      );
    }

    if (newPage <= 0 && this.currentChunkIndex === 0) {
      this.topPageSnapshotElement.classList.add('hide-snapshot-content');
    }
    if (newPage >= this.pageRects.length - 1 && this.isCurrentChunkIndexFinal) {
      this.bottomPageSnapshotElement.classList.add('hide-snapshot-content');
    }
  }

  refreshPageSnapshotsForCurrentPage() {
    this.refreshPageSnapshotsForPage(this.currentPage, true);
  }

  getBoundedPageNumWithinChunk(pageNum: number) {
    if (this.pageRects.length - 1 < pageNum) {
      this.computePageRects(pageNum + 3);
    }

    // return a value from either zero to up to pageRects; we should have all the pageRects computed
    // up to this pageNum, if the page num was greater than all the pages we could compute, return the last page
    return Math.min(Math.max(0, pageNum), this.pageRects.length - 1);
  }

  getBoundedPageRect(pageNum: number): PageRect {
    if (this.pageRects.length <= pageNum) {
      this.computePageRects(pageNum);
    }

    if (pageNum < 0 && this.currentChunkIndex > 0) {
      // Return the previous chunks last page
      const previousChunk = this.currentChunkIndex - 1;
      const previousChunkElement = this.getChunkContainerByIndex(previousChunk);
      if (this.previousChunkPageRects.length === 0) {
        this.previousChunkPageRects = [this.buildFirstPageRect(previousChunkElement)];
      }
      this.computePageRectsForChunk(previousChunk, 100000, this.previousChunkPageRects);
      const pageRectIndexToReturn = Math.max(0, this.previousChunkPageRects.length + pageNum);
      return this.previousChunkPageRects[pageRectIndexToReturn];
    }

    if (pageNum < 0) {
      // We are in chunk index 0
      this.computePageRects(0);
      return this.pageRects[0];
    }

    // At this point we should have computed all the pages up to pageNum or max pages
    // if pageNum is greater than max page number, return the last page
    const pageNumToReturn = Math.min(pageNum, this.pageRects.length - 1);
    const pageRectToReturn = this.pageRects[pageNumToReturn];
    if (pageNum >= this.pageRects.length && this.currentChunkIndex + 1 < this.numberOfChunks) {
      // Return the next chunks first page
      const nextChunkIndex = this.currentChunkIndex + 1;
      const nextChunkElement = this.getChunkContainerByIndex(nextChunkIndex);
      const chunkRects = [this.buildFirstPageRect(nextChunkElement)];
      this.computePageRectsForChunk(nextChunkIndex, 1, chunkRects);
      return chunkRects[0];
    }
    return pageRectToReturn;
  }

  movePageBorder(borderId: string, newTop: number, dividerHeight: number, shouldHideBorder: boolean) {
    const pageDivider = this.document.querySelector<HTMLElement>(`#page-divider-${borderId}`);

    if (!pageDivider) {
      throw new ScrollingManagerError(`No element with ID #page-divider-${borderId} found`);
    }
    pageDivider.style.top = `${newTop}px`;
    if (dividerHeight === 0 || shouldHideBorder) {
      pageDivider.classList.add('non-splittable-divider');
    } else {
      pageDivider.classList.remove('non-splittable-divider');
      pageDivider.style.height = `${dividerHeight + 1}px`;
    }
  }

  refreshAllPaginationElements(currentPageNum: number) {
    this.hideVisiblePageDividerElements();

    if (!this.bottomPageSnapshotElement || !this.topPageSnapshotElement) {
      throw new ScrollingManagerError(
        '[ScrollToPage] No page snapshot elements found',
      );
    }

    const previousPageRect = this.getBoundedPageRect(currentPageNum - 1);
    const pageRect = this.getBoundedPageRect(currentPageNum);
    const nextPageRect = this.getBoundedPageRect(currentPageNum + 1);

    const previousPreviousPageRectHeight = this.pageHeight * 3;
    const previousPreviousPageRectTop = previousPageRect.top - previousPageRect.bottomPageDividerHeight - previousPreviousPageRectHeight;

    let shouldHideBorderAbovePreviousPage = currentPageNum <= 1 && this.currentChunkIndex === 0;

    /*
     we want to handle a special case where we should hide the border above the previous page
     This is most likely in the situation where we are in the chunk right after the first one
     and the first chunk is only one page long
    */
    if (currentPageNum === 0 && this.currentChunkIndex === 1) {
      const previousPreviousPageRect = this.getBoundedPageRect(currentPageNum - 2);
      if (previousPageRect.top === previousPreviousPageRect.top) {
        // In this case, we have confirmed that the chunk above (chunk zero) is only one page long
        shouldHideBorderAbovePreviousPage = true;
      }
    }

    this.movePageBorder(
      'border-above-previous-page',
      previousPreviousPageRectTop,
      previousPreviousPageRectHeight,
      shouldHideBorderAbovePreviousPage,
    );
    this.movePageBorder(
      'border-above-current-page',
      pageRect.top - previousPageRect.bottomPageDividerHeight,
      previousPageRect.bottomPageDividerHeight,
      currentPageNum === 0 && this.currentChunkIndex === 0,
    );
    this.movePageBorder(
      'border-below-current-page',
      pageRect.bottom,
      pageRect.bottomPageDividerHeight,
      currentPageNum >= this.pageRects.length - 1 && this.isCurrentChunkIndexFinal,
    );
    // If we are at a chunk border, the final border height is not entirely clear yet; make it extra tall
    const borderBelowNextPageHeight = currentPageNum + 1 >= this.pageRects.length ? this.pageHeight * 3 : nextPageRect.bottomPageDividerHeight;
    this.movePageBorder(
      'border-below-next-page',
      nextPageRect.bottom + pageRect.bottomPageDividerHeight,
      borderBelowNextPageHeight,
      currentPageNum >= this.pageRects.length - 1 && this.isCurrentChunkIndexFinal,
    );

    if (this.headerContainer) {
      if (currentPageNum === 0 && this.currentChunkIndex === 0) {
        this.headerContainer.style.top = '0px';
      } else {
        const firstPageRect = this.getBoundedPageRect(-1);
        // offset the header by the size of the first pages bottom border
        this.headerContainer.style.top = `${-firstPageRect.bottomPageDividerHeight}px`;
      }
    }

    const newScrollTop = currentPageNum <= 0 && this.currentChunkIndex === 0 ? 0 : pageRect.top - this.topMargin;
    this.setScrollingElementTop(newScrollTop);

    // re-simulate top and bottom content snapshots
    this.refreshPageSnapshotsForPage(currentPageNum);

    if (currentPageNum >= this.pageRects.length - 1 && this.isCurrentChunkIndexFinal) {
      animateEndOfReadingButton(0);
    }
  }

  async onScrollToPageEnd(newPage: number) {
    this.logger.log(`On scroll to page end, newPage: ${newPage}`);

    this.currentPage = newPage;

    this.hideVisiblePageDividerElements();
    this.refreshAllPaginationElements(newPage);

    this.updateCurrentCenteredElement();

    await this.onScrollEnd();

    for (const func of this.scrollListeners) {
      func({
        currentCenterElementInfo: this.currentCenteredElementInfo,
        currentChunkElement: this.getCurrentChunkContainer(),
        currentPage: this.currentPage,
      });
    }
    this.enableAllTouchEvents(TouchEventDisabledSource.Scroll);
    this.currentlyTransitioningPages = false;
  }

  async onScrollToPageStart(newPage: number) {
    if (!this.bottomPageSnapshotElement || !this.topPageSnapshotElement) {
      throw new ScrollingManagerError('[ScrollToPage] No page snapshot elements found');
    }

    await this.setNewChunkIndex(this.currentChunkIndex, 1);
    this.logger.log(`We want to scroll to page ${newPage}`);
    this.computePageRects(newPage);

    if (newPage > 1) {
      this.headerImageContainer?.classList.add('hide-snapshot-content');
    } else {
      this.headerImageContainer?.classList.remove('hide-snapshot-content');
    }
  }

  async emitScrollStartEvent() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    this.updateCurrentCenteredElement();
    const serializedPositionInfo = this.computeSerializedPositionFromCenteredElement();

    this.initialPageOnScrollStart = this.currentPage;

    const scrollDepthWithinCurrentChunk = this.getScrollDepthWithinCurrentChunk();
    const readingProgressData = await this.getReadingPositionData();

    return this.window.portalGateToForeground.emit('scroll_start', {
      scrollDelta: 0,
      currentScrollPositionData: {
        chunkIndex: this.currentChunkIndex,
        serializedPosition: serializedPositionInfo?.serializedPosition,
        serializedElementVerticalOffset: serializedPositionInfo?.serializedPositionElementOffset,
        scrollDepthWithinChunk: scrollDepthWithinCurrentChunk,
      },
      currentReadingPositionData: readingProgressData,
    });
  }


  async setNewChunkIndex(newIndex: number, pageRectsToCompute: number) {
    if (newIndex === this.currentChunkIndex) {
      return;
    }
    const newChunkIndex = Math.min(Math.max(0, newIndex), this.numberOfChunks - 1);
    const currentChunkElement = this.getChunkContainerByIndex(newChunkIndex);
    this.onContentChanged();

    if (newIndex > 0 && this.headerImageContainer) {
      this.headerImageContainer.style.display = 'none';
      this.headerImageContainer.style.opacity = '0';
    } else if (this.headerImageContainer) {
      this.headerImageContainer.style.display = 'block';
      setTimeout(() => {
        if (this.headerImageContainer) {
          this.headerImageContainer.style.opacity = '1';
        }
      }, 10);
    }

    await this._loadOnlyContentSurroundingChunkContainer(currentChunkElement);
    this.currentChunkIndex = newChunkIndex;

    this.forcedBreakCoords = this._scanForcedBreaksForAllLoadedChunks();

    this.currentChunkHeight = currentChunkElement.scrollHeight;
    this.currentChunkTop = this.getAbsoluteScrollTopOfRange(currentChunkElement);
    this.pageRects = [];
    this.nextChunkPageRects = [];
    this.previousChunkPageRects = [];
    this.computePageRects(pageRectsToCompute);
  }

  async scrollToCoordSmooth(yCoord: number, initialVelocity = 0) {
    if (this.window.osType === 'ios') {
      this.scrollingElementScrollTo({ top: yCoord, behavior: 'smooth' });
      if (this.hapticsOnScrollEnabled) {
        setTimeout(() => {
          this.window.portalGateToForeground.emit('haptics_feedback');
        }, this.iosScrollDelay - this.hapticsOnScrollTimeModifier);
      }
      await delay(this.iosScrollDelay);
    } else {
      await this.fakeSmoothScrollToCoord(yCoord, initialVelocity);
    }
  }

  async scrollToPageSmooth(newPageParam: number, initialVelocity = 0, shouldLogForDebug = false): Promise<void> {
    const logger = makeWebviewLogger('scrollToPageSmooth', {
      shouldLog: shouldLogForDebug,
    });
    if (this.debugFreeScroll || this.pageRects.length === 0) {
      return;
    }
    if (!this.bottomPageSnapshotElement || !this.topPageSnapshotElement) {
      throw new ScrollingManagerError('[ScrollToPage] No page snapshot elements found');
    }
    if (!this.documentRoot) {
      throw new ScrollingManagerError('[ScrollToPage] No document root found');
    }
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('[ScrollToPage] No document text content found');
    }
    const currentPage = this.currentPage;
    const newPage = newPageParam;
    const direction = currentPage < newPage ? 'down' : 'up';

    if (this.smoothAnimationsDisabled) {
      for (const promise of this.currentTransitionPromises) {
        try {
          promise.resolve(true);
        } catch (e) {

          /* empty */
        }
      }
      return this._scrollToPage(newPage);
    }

    await Promise.all(this.currentTransitionPromises);

    this.disableAllTouchEvents(TouchEventDisabledSource.Scroll);
    await this.onScrollToPageStart(newPage);

    const scrollingToSamePage = currentPage === newPage;
    logger.log(`Scroll to page ${newPage} currentPage ${currentPage} smooth currentChunk ${this.currentChunkIndex}`);

    this.currentlyTransitioningPages = true;

    this.currentPage = newPage;
    const currentPageRect = this.getBoundedPageRect(newPage);
    let newScrollTop = newPage <= 0 && this.currentChunkIndex === 0 ? 0 : currentPageRect.top - this.topMargin;

    const previousPageRect = this.getBoundedPageRect(newPage - 1);

    /*
       If we are about to scroll to the first chunk, we should check if the first chunk only has one page
       in that case, the new scroll top should be zero
    */
    if (newPage < 0 && this.currentChunkIndex === 1) {
      const previousPreviousPageRect = this.getBoundedPageRect(newPage - 2);
      if (previousPageRect.top === previousPreviousPageRect.top) {
        // In this case, we have confirmed that the chunk above (chunk zero) is only one page long
        newScrollTop = 0;
      }
    }

    const topElementOffset = currentPageRect.bottomPageDividerHeight;
    const bottomElementOffset = previousPageRect.bottomPageDividerHeight;

    let initialScrollTarget = newScrollTop;
    if (direction === 'down') {
      if (previousPageRect.bottomPageDividerHeight !== SPLIT_BORDER_HEIGHT) {
        initialScrollTarget = newScrollTop + bottomElementOffset + this.pageScrollOffset;
      }
    } else {
      initialScrollTarget = newScrollTop - topElementOffset - this.pageScrollOffset;
    }


    // If we are not on the first page, move the border up a bit
    // so when we reach the first page, its correctly offset until we finish scrolling to first page
    if (currentPage !== 0 && this.headerContainer) {
      const firstPageRect = this.getBoundedPageRect(-1);
      // offset the header by the size of the first pages bottom border
      this.headerContainer.style.top = `${-firstPageRect.bottomPageDividerHeight}px`;
    }

    if (scrollingToSamePage || newPage < 0 && this.currentChunkIndex === 0) {
      initialScrollTarget = newScrollTop;
    }

    await this.scrollToCoordSmooth(initialScrollTarget, initialVelocity);
    this.setScrollingElementTop(initialScrollTarget);

    if (newPageParam < 0) {
      if (this.currentChunkIndex === 0) {
        return this._scrollToPage(0);
      }
      await this.setNewChunkIndex(this.currentChunkIndex - 1, 10000);
      this.currentPage = this.pageRects.length - 1;
      return this._scrollToPage(this.currentPage);
    }
    if (newPageParam >= this.pageRects.length && this.currentChunkIndex + 1 < this.numberOfChunks) {
      await this.setNewChunkIndex(this.currentChunkIndex + 1, 10);
      this.currentPage = 0;
      return this._scrollToPage(this.currentPage);
    }

    if (newPageParam >= this.pageRects.length) {
      return this._scrollToPage(this.pageRects.length - 1);
    }

    // ON SCROLL END
    await this.onScrollToPageEnd(newPage);
  }

  onScrollStart() {
    this.previousScrollValue = this.currentScrollValue;
    this.emitScrollStartEvent();
  }

  onScroll() {}


  async onScrollEnd() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    const currentScrollValue = this.getScrollingElementTop();
    if (currentScrollValue === this.currentScrollValue) {
      return;
    }
    this.currentScrollValue = currentScrollValue;

    const readingProgressData = await this.getReadingPositionData();

    const serializedPositionInfo = this.computeSerializedPositionFromCenteredElement();
    const scrollDepthWithinCurrentChunk = this.getScrollDepthWithinCurrentChunk();
    const scrollDelta = this.currentScrollValue - this.previousScrollValue;

    this.previousScrollValue = this.currentScrollValue;
    return this.window.portalGateToForeground.emit('scroll_end', {
      currentScrollPositionData: {
        chunkIndex: this.currentChunkIndex,
        serializedPosition: serializedPositionInfo?.serializedPosition,
        serializedElementVerticalOffset: serializedPositionInfo?.serializedPositionElementOffset,
        scrollDepthWithinChunk: scrollDepthWithinCurrentChunk,
      },
      currentReadingPositionData: readingProgressData,
      scrollDelta,
    });
  }

  async scrollToReadingPosition(readingPosition: LenientReadingPosition) {
    this.logger.log('Scroll To Reading Position ', readingPosition);
    await super.scrollToReadingPosition(readingPosition);
    this.startTouchClientY = null;
    this.enableAllTouchEvents(TouchEventDisabledSource.Scroll);
  }

  isDocumentScrolledToBeginning(): boolean {
    return this.getScrollingElementTop() < 100;
  }

  scrollToRect(rect: DOMRect, offset = 0) {
    const newTop = Math.floor(this.getScrollingElementTop() + rect.top) + offset;
    this.logger.log(`Scroll to rect ${newTop}`);
    const newPageNumber = this.getPageNumberFromCoordinate(newTop);
    this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: newPageNumber, disableAnimation: true });
  }

  scrollToTop() {}
  async scrollToStartOfDocument(landmarks?: EpubLandmark[], pageList?: EpubPageListItem[]) {
    try {
      await this.scrollToEpubStartPosition(landmarks, pageList);
    } catch {
      // Fallback to existing behavior
      await this.setNewChunkIndex(0, 1);
      await this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: 0, disableAnimation: true });
    }
  }


  // thus CSS top positions and pageRect might not always align asCSS top positions are relative to the coordinates of their parents
  getPageNumberFromCoordinate(yCoord: number) {
    this.logger.log(`getPageNumberFromCoordinate ${yCoord}`);
    const firstPageRect = this.getBoundedPageRect(0);
    if (firstPageRect.top > yCoord) {
      return 0;
    }

    // If we were to scroll to this ycoord, what page is most visible?

    for (let i = 0; i < this.pageRects.length; i++) {
      const pageRect = this.getBoundedPageRect(i);
      if (pageRect.top <= yCoord && pageRect.bottom >= yCoord) {
        this.logger.log(`found page ${i}`);
        return i;
      }
    }
    this.logger.log(
      'We tried to find the page coordinate but we ran out of pages, probably need to make more pages?',
    );
    this.logger.log(`number of pages computed: ${this.pageRects.length}`);
    this.logger.log('Estimate how many more pages we need ');
    this.logger.log(
      `yCoord: ${yCoord} pageHeight: ${this.pageHeight} page num to generate: ${Math.ceil(
        yCoord / (this.pageHeight / 2),
      )}`,
    );
    // TODO: This process is pretty error prone, we kind of assume how many pages we need to compute
    // before we find the coord in question, however the better way would be to compute pages UNITL we find the coord
    this.computePageRects(Math.ceil(yCoord / (this.pageHeight / 2)));

    for (let i = 0; i < this.pageRects.length; i++) {
      if (this.pageRects[i].top - this.topMargin > yCoord) {
        this.logger.log(`found page  ${i - 1}`);
        return i - 1;
      }
    }
    this.logger.log(` here are all the page rects ${this.pageRects}`);
    this.logger.log(`found last page  ${this.pageRects.length - 1}`);
    return this.pageRects.length - 1;
  }

  // It's important to note that pageRect is relative to the entire document
  handleScrollFromHref() {
    const newPage = this.getPageNumberFromCoordinate(this.getScrollingElementTop());
    this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: newPage, disableAnimation: true });
  }

  scrollViewportToCurrentTTSLocation(rect: DOMRect) {
    if (this.ttsAutoScrollingEnabled) {
      // The offset helps make sure the TTS element stays on the right page
      this.scrollToRect(rect, rect.height - 2);
    }
  }

  async returnToReadingPosition() {
    if (this.readingPosition) {
      await this.scrollToReadingPosition(this.readingPosition);
    }
    await delay(4);
    if (!this.documentRoot) {
      throw new ScrollingManagerError('No document root found');
    }
    const serializedPositionInfo = this.computeSerializedPositionFromCenteredElement();
    const scrollDepthWithinCurrentChunk = this.getScrollDepthWithinCurrentChunk();
    const readingProgressData = await this.getReadingPositionData();
    this.window.portalGateToForeground.emit('return_to_reading_position', {
      currentScrollPositionData: {
        chunkIndex: this.currentChunkIndex,
        serializedPosition: serializedPositionInfo?.serializedPosition,
        serializedElementVerticalOffset: serializedPositionInfo?.serializedPositionElementOffset,
        scrollDepthWithinChunk: scrollDepthWithinCurrentChunk,
      },
      currentReadingPositionData: readingProgressData,
      scrollDelta: 0,
    });
  }

  clearAllTouchData() {
    this.currentlyScrollingBecauseOfTouch = false;
    this.startPageOnTouch = null;
    this.startTouchClientY = null;
    this.scrollStartTime = 0;
    this.hasTouchMoved = false;
    this.touchMoveThrottle = 0;
    this.touchYDirection = 0;
    this.touchDirectionSwapCounter = 0;
  }

  async instantlyScrollPageUp() {
    this.queueScrollToPageEvent({ direction: 'up', velocity: 0, disableAnimation: true });
    this.window.portalGateToForeground.emit('touch_move');
  }

  instantlyScrollPageDown() {
    this.queueScrollToPageEvent({ direction: 'down', velocity: 0, disableAnimation: true });
    this.window.portalGateToForeground.emit('touch_move');
  }

  async emitScrollEvent() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    const currentScrollValue = this.getScrollingElementTop();
    this.currentScrollValue = currentScrollValue;

    const serializedPositionInfo = this.computeSerializedPositionFromCenteredElement();
    const scrollDepthWithinCurrentChunk = this.getScrollDepthWithinCurrentChunk();
    const readingProgressData = await this.getReadingPositionData();
    const scrollDelta = this.currentScrollValue - this.previousScrollValue;
    return this.window.portalGateToForeground.emit('scroll', {
      currentScrollPositionData: {
        chunkIndex: this.currentChunkIndex,
        serializedPosition: serializedPositionInfo?.serializedPosition,
        serializedElementVerticalOffset: serializedPositionInfo?.serializedPositionElementOffset,
        scrollDepthWithinChunk: scrollDepthWithinCurrentChunk,
      },
      currentReadingPositionData: readingProgressData,
      scrollDelta,
    });
  }

  onTouchStart(e: TouchEvent) {
    if (!this.documentRoot) {
      throw new ScrollingManagerError('ontouchStart, Document root not found');
    }
    if (e.touches.length === 1) {
      this.window.portalGateToForeground.emit('touch_start');
      this.showVisiblePageDividerElements();
      this.scrollStartTime = nowTimestamp();
      this.hasTouchMoved = false;
      this.startPageOnTouch = this.currentPage;
      this.touchYDirection = 0;
      this.startTouchClientY = e.touches[0].clientY;
      this.endTouchClientY = e.touches[0].clientY;

      this.startTouchX = e.touches[0].clientX;
      this.endTouchX = this.startTouchX;

      this.emitScrollStartEvent();
    }
  }

  onTouchMove(e: TouchEvent) {
    if (this.startTouchClientY === null) {
      // should never happen
      return;
    }

    const touch = e.touches[0];

    if (getIsCurrentlyUsingStylus() && !isActuallyMovingWithStylus({ touchFromStylus: touch, startTouchClientY: this.startTouchClientY })) {
      return;
    }

    if (this.ttsAutoScrollingEnabled) {
      this.window.portalGateToForeground.emit('set_tts_auto_scrolling_enabled', false);
    }
    this.currentlyScrollingBecauseOfTouch = true;
    if (this.touchMoveThrottle === 0) {
      this.window.portalGateToForeground.emit('touch_move');
    }
    const newClientY = touch.clientY;

    if (this.areTouchesDisabledBecauseOfSelection) {
      this.endTouchClientY = newClientY;
      return;
    }

    this.touchMoveThrottle += 1;
    this.hasTouchMoved = true;
    if (this.touchMoveThrottle > 2) {
      // this.logger.log(`newTouch ${newClientY} oldTouch: ${this.ongoingTouchClientY} `);
      this.touchMoveThrottle = 0;
      this.emitScrollEvent();
    }
    const touchYDelta = Math.abs(this.endTouchClientY - newClientY);
    if (touchYDelta > 0) {
      const direction = (this.endTouchClientY - newClientY) / touchYDelta;
      if (this.touchYDirection !== direction) {
        this.logger.log(`Direction changed!! new startY ${newClientY}`);
        if (this.touchYDirection === 0) {
          this.touchYDirection = direction;
        }
        this.touchDirectionSwapCounter += 1;
        if (this.touchDirectionSwapCounter > 1) {
          this.touchDirectionSwapCounter = 0;
          this.scrollStartTime = nowTimestamp();
          this.touchYDirection = direction;
        }
      }
    }
    this.endTouchClientY = newClientY;
    this.endTouchX = touch.clientX;
    if (this.enableScrollTimeout) {
      clearTimeout(this.enableScrollTimeout);
    }
  }

  onTouchCancel(e: TouchEvent) {
    this.onTouchEnd(e);
  }

  onTouchEnd(e: TouchEvent) {
    this.window.portalGateToForeground.emit('touch_end');
    if (this.startTouchClientY === null || this.startPageOnTouch === null) {
      // Very strange case but might happen;
      this.clearAllTouchData();
      this.queueScrollToPageEvent({ direction: null, velocity: 0.4, disableAnimation: true });
      return;
    }

    this.hideVisiblePageDividerElements();
    const scrollStartTime = this.scrollStartTime;
    const currentTime = nowTimestamp();
    const timeDelta = currentTime - scrollStartTime;
    const yTouchDelta = this.startTouchClientY - this.endTouchClientY;
    let verticalDelta: number | null = null;

    if (this.areTouchesDisabledBecauseOfSelection) {
      this.logger.log('Touches are disabled because of selection');
      this.clearAllTouchData();
      return;
    }

    if (this.touchYDirection < 0 && yTouchDelta < 0 || this.touchYDirection > 0 && yTouchDelta > 0) {
      // we need to check that the direction we ended up swiping also reflects the amount of screen drag we created
      // if we dragged up, touchYDirection is -1, and if directionalTouchDelta is negative that means the screen moved up from the start touch spot
      // if this wasnt true, then we did not move enough to justify an entire page swipe
      verticalDelta = yTouchDelta;
    }

    const horizontalDelta = Math.abs(this.startTouchX - this.endTouchX);
    const isHorizontalSwipe =
      horizontalDelta > this.horizontalSwipeDistanceThreshold &&
      horizontalDelta > (verticalDelta ?? 0) &&
      Math.abs(verticalDelta ?? 0) < this.verticalSwipeDistanceConclusiveThreshold;

    let velocity = verticalDelta ? Math.abs(verticalDelta / timeDelta) : 0.04;
    if (!Number.isFinite(velocity)) {
      velocity = 0.04;
    }

    this.logger.log(
      `On touch end - startY: ${this.startTouchClientY} end: ${this.endTouchClientY} touchDelta ${verticalDelta} direction ${this.touchYDirection} velocity ${velocity} timeDelta ${timeDelta} horizontal delta: ${horizontalDelta} isHorizontalSwipe: ${isHorizontalSwipe}`,
    );

    const isTapOnScreen = !this.hasTouchMoved && timeDelta < 300;
    this.logger.log('IS TAP ON SCREEN ', { isTapOnScreen, touchMoved: this.hasTouchMoved });

    if (isTapOnScreen) {
      let target = null;
      if (e.changedTouches.length > 0) {
        target = e.changedTouches[0].target as Node;
      }
      if (
        this.isTouchTargetClickableOnMargins(target) ||
        this.isTouchTargetClickableOnMargins(target?.parentElement)
      ) {
        this.hideVisiblePageDividerElements();
        this.clearAllTouchData();
        return;
      }
      if (this.endTouchX <= this.leftClickAreaWidth) {
        this.window.portalGateToForeground.emit('haptics_feedback');
        this.instantlyScrollPageUp();
        this.clearAllTouchData();
        e.preventDefault();
        e.stopPropagation();
        return;
      } else if (this.endTouchX >= this.rightClickAreaWidth) {
        // Press on right side
        this.window.portalGateToForeground.emit('haptics_feedback');
        this.instantlyScrollPageDown();
        this.clearAllTouchData();
        e.preventDefault();
        e.stopPropagation();
        return;
      } else {
        // Code that handles a general press on the center of screen
        this.hideVisiblePageDividerElements();
        this.clearAllTouchData();
        // If we click on a tag that has some kind of click handler, handle that
        if (target && isHTMLElement(target) && CLICKABLE_TAGS.has(target.nodeName)) {
          return;
        }
        this.clearAllTouchData();
        this.window.portalGateToForeground.emit('root-clicked');
        return;
      }
    }

    // Here we actually commit the scroll
    let direction: 'down' | 'up' | null = null;
    if (
      !isHorizontalSwipe && verticalDelta !== null &&
      (verticalDelta > this.verticalSwipeDistanceMinimumThreshold ||
        verticalDelta > 0 && velocity > this.velocityThreshold)
    ) {
      direction = 'down';
    } else if (
      !isHorizontalSwipe && verticalDelta !== null &&
      (verticalDelta < -this.verticalSwipeDistanceMinimumThreshold ||
        verticalDelta < 0 && velocity > this.velocityThreshold)
    ) {
      direction = 'up';
    }
    this.logger.log('add new scroll event to parse ', { verticalDelta, velocity, direction });

    this.clearAllTouchData();
    // Disable the animation if we barely scrolled; a instant animation is much more responsive
    this.queueScrollToPageEvent({ direction, velocity, disableAnimation: Math.abs(yTouchDelta) < 30 });
  }

  async processAllScrollEvents() {
    if (this.currentlyProcessingScrollEvents) {
      return;
    }
    this.logger.log('Processing scroll events ', { events: this.scrollEventsToProcess });
    while (this.scrollEventsToProcess.length) {
      this.currentlyProcessingScrollEvents = true;

      const currentEvent = this.scrollEventsToProcess.shift();
      if (!currentEvent) {
        break;
      }

      const { direction } = currentEvent;
      // lets find all the events with the same direction and purge them
      while (this.scrollEventsToProcess.length) {
        if (this.scrollEventsToProcess[0].direction === direction) {
          this.scrollEventsToProcess.shift();
        } else {
          break;
        }
      }
      let newPage = this.currentPage + (currentEvent.direction === 'down' ? 1 : -1);
      if (currentEvent.direction === null) {
        newPage = currentEvent.specificPage ?? this.currentPage;
      }
      if (currentEvent.disableAnimation) {
        await this._scrollToPage(newPage);
      } else {
        await this.scrollToPageSmooth(newPage, currentEvent.velocity || 0.1);
      }
      await delay(2);
    }
    this._debugEndScrollToPageTime = nowTimestamp();
    this.currentlyProcessingScrollEvents = false;
    if (this.deferResizeUntilAllScrollEventsAreDone) {
      this.deferResizeUntilAllScrollEventsAreDone = false;
      this.updateCurrentCenteredElement();
      await this.onResize(true);
    }
  }

  getChunkScrollDepth(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect): number {
    return getChunkScrollDepthInMobile(currentChunkElement, serializedPositionElement);
  }

  /**
   * Scan a chunk for CSS `page-break-*` / `break-*` directives.
   *
   * This function traverses the chunk element and its children, looking for elements that have a CSS `page-break-*` / `break-*` directive.
   * This is quite expensive as we need to use computed styles to check for the directives. We cache the results in `this.forcedBreaksCache` and only recompute them if the layout changes.
   */
  private _scanForcedBreaksForChunk(chunkElement: ChunkContainerElement): ChunkRelativeBreak[] {
    const breaks: ChunkRelativeBreak[] = [];
    const chunkIndex = parseInt(chunkElement.dataset.chunkIndex || '0', 10);
    const chunkTop = this.getAbsoluteScrollTopOfRange(chunkElement);

    const walker = this.document.createTreeWalker(chunkElement, NodeFilter.SHOW_ELEMENT);

    while (walker.nextNode()) {
      const el = walker.currentNode as HTMLElement;
      if (!el || !el.getBoundingClientRect) {
        continue;
      }

      const style = this.window.getComputedStyle(el);
      const beforeValue =
        style.getPropertyValue('page-break-before') || style.getPropertyValue('break-before');
      if (beforeValue === 'always' || beforeValue === 'page') {
        const absoluteY = this.getAbsoluteScrollTopOfRange(el);
        const offsetInChunk = absoluteY - chunkTop;
        breaks.push({
          chunkIndex,
          offsetInChunk,
          breakType: 'before',
        });
      }
      const afterValue =
        style.getPropertyValue('page-break-after') || style.getPropertyValue('break-after');
      if (afterValue === 'always' || afterValue === 'page') {
        const absoluteY = this.getAbsoluteScrollTopOfRange(el) + el.getBoundingClientRect().height;
        const offsetInChunk = absoluteY - chunkTop;
        breaks.push({
          chunkIndex,
          offsetInChunk,
          breakType: 'after',
        });
      }
    }

    breaks.sort((a, b) => a.offsetInChunk - b.offsetInChunk);
    return breaks;
  }

  private _getAbsoluteCoordFromChunkRelativeBreak(breakCoord: ChunkRelativeBreak): number {
    const chunkElement = this.getChunkContainerByIndex(breakCoord.chunkIndex);
    const chunkTop = this.getAbsoluteScrollTopOfRange(chunkElement);
    return chunkTop + breakCoord.offsetInChunk;
  }

  private _isBreakRedundant(breakCoord: ChunkRelativeBreak, pageRect: PageRect): boolean {
    const absoluteBreakCoord = this._getAbsoluteCoordFromChunkRelativeBreak(breakCoord);
    const tolerance = 5;

    if (breakCoord.breakType === 'before') {
      return Math.abs(absoluteBreakCoord - pageRect.top) <= tolerance;
    } else {
      return Math.abs(absoluteBreakCoord - pageRect.bottom) <= tolerance;
    }
  }

  private _scanForcedBreaksForAllLoadedChunks(): ChunkRelativeBreak[] {
    try {
      const allBreaks: ChunkRelativeBreak[] = [];


      const chunksToScan = [
        this.currentChunkIndex - 1,
        this.currentChunkIndex,
        this.currentChunkIndex + 1,
      ].filter((index) => index >= 0 && index < this.numberOfChunks);

      for (const chunkIndex of chunksToScan) {
        const cacheKey = chunkIndex.toString();

        if (this.forcedBreaksCache[cacheKey]) {
          allBreaks.push(...this.forcedBreaksCache[cacheKey]);
        } else {
          const chunkElement = this.getChunkContainerByIndex(chunkIndex);
          if (chunkElement && chunkElement.dataset.chunkIndex !== undefined) {
            const chunkBreaks = this._scanForcedBreaksForChunk(chunkElement);
            this.forcedBreaksCache[cacheKey] = chunkBreaks;
            allBreaks.push(...chunkBreaks);
          }
        }
      }

      allBreaks.sort((a, b) => {
        if (a.chunkIndex !== b.chunkIndex) {
          return a.chunkIndex - b.chunkIndex;
        }
        return a.offsetInChunk - b.offsetInChunk;
      });

      return allBreaks;
    } catch (error) {
      this.logger.error('Failed to scan forced breaks for loaded chunks:', { error });
      return [];
    }
  }

  private _calculateAndApplyDividerHeight(
    currentPageRect: PageRect,
    currentPageNum: number,
    relativeTopOfDocumentTextContent: number,
    additionalDividerHeight = 0,
  ): void {
    let dividerHeight = this.pageHeight + this.bottomMargin - (currentPageRect.bottom - currentPageRect.top);

    if (currentPageNum === 0 && this.currentChunkIndex === 0) {
      // uncomment this if we want a divider on the first page
      dividerHeight -= relativeTopOfDocumentTextContent - this.topMargin;
      // uncomment this if we want the divider under the first page to be hidden
      // dividerHeight = 0;
    }

    dividerHeight = Math.max(0, dividerHeight + 1 + additionalDividerHeight);

    if (dividerHeight !== SPLIT_BORDER_HEIGHT) {
      currentPageRect.bottomPageDividerHeight = dividerHeight + 100;
    } else {
      currentPageRect.bottomPageDividerHeight = SPLIT_BORDER_HEIGHT;
    }
  }

  async _scrollToScrollDepthPercentOfChunk(chunkIndex: number, scrollDepthWithinChunk: number) {
    await this.setNewChunkIndex(chunkIndex, 1);
    const chunkContainer = this.getChunkContainerByIndex(chunkIndex);
    const chunkHeight = chunkContainer.getBoundingClientRect().height;
    const absoluteTopOfChunkContainer = this.getAbsoluteScrollTopOfRange(chunkContainer);
    const coordinateToScrollTo = absoluteTopOfChunkContainer + Math.min(chunkHeight - this.pageHeight, chunkHeight * scrollDepthWithinChunk);
    const pageNum = this.getPageNumberFromCoordinate(coordinateToScrollTo);
    await this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: pageNum, disableAnimation: true });
  }


  _isScrolledToEndOfChunk() {
    this.computePageRects(this.currentPage);
    return this.currentPage >= this.pageRects.length - 1;
  }

  async _scrollToRangeOrElement(target: HTMLElement | Range, offset = 0) {
    let chunkContainer: ChunkContainerElement | undefined;
    if (target instanceof Range) {
      // NOTE: there's an edge case here where a range encompasses >1 chunk but we only get the first container
      chunkContainer = this.findChunkContainerForElement(this.getFirstNodeInRange(target));
    } else {
      chunkContainer = this.findChunkContainerForElement(target);
    }
    if (!chunkContainer) {
      return;
    }

    const newChunkIndex = parseInt(chunkContainer.dataset.chunkIndex, 10);
    await this.setNewChunkIndex(newChunkIndex, 1);

    const y = this.getAbsoluteScrollTopOfRange(target);

    this.currentPage = this.getPageNumberFromCoordinate(y - offset);
    await this.queueScrollToPageEvent({ direction: null, velocity: 0, disableAnimation: true });
  }

  // Most likely you want to call queueScrollToPageEvent, be careful calling this directly
  private async _scrollToPage(newPageParam: number): Promise<void> {
    if (this.debugFreeScroll) {
      return;
    }

    // Since we didn't trigger this via a scroll, we should fire these events here
    await this.emitScrollStartEvent();

    this.computePageRects(newPageParam);

    const currentScrollPos = this.getScrollingElementTop();

    if (newPageParam < 0) {
      if (this.currentChunkIndex === 0) {
        return this._scrollToPage(0);
      }
      await this.setNewChunkIndex(this.currentChunkIndex - 1, 10000);
      this.currentPage = this.pageRects.length - 1;
      return this._scrollToPage(this.currentPage);
    }
    if (newPageParam >= this.pageRects.length && this.currentChunkIndex + 1 < this.numberOfChunks) {
      await this.setNewChunkIndex(this.currentChunkIndex + 1, 10);
      this.currentPage = 0;
      return this._scrollToPage(this.currentPage);
    }

    if (newPageParam >= this.pageRects.length) {
      return this._scrollToPage(Math.max(0, this.pageRects.length - 1));
    }

    const newPage = this.getBoundedPageNumWithinChunk(newPageParam);

    const currentPage = this.currentPage;
    this.currentPage = newPage;

    const pageRect = this.getBoundedPageRect(newPage);
    const newScrollTop = newPage <= 0 && this.currentChunkIndex === 0 ? 0 : pageRect.top - this.topMargin;
    this.logger.log(`Scroll to page ${newPage}`, { newScrollTop, pageRect });

    const direction = currentPage < newPage ? 'down' : 'up';
    let initialScrollTarget = 0;
    if (direction === 'down') {
      initialScrollTarget = newScrollTop;
    } else {
      initialScrollTarget = Math.max(0, newScrollTop);
    }

    if (currentScrollPos === initialScrollTarget) {
      await this.onScrollToPageEnd(this.currentPage);
      return;
    }

    await this.onScrollToPageStart(newPage);
    this.setScrollingElementTop(initialScrollTarget);
    await this.onScrollToPageEnd(newPage);
  }


}
